import { IMAGE_DEFAULT } from '@/main/services/constants';
import { store } from '..';
import { Cart, CartPart, CartState, ShipingCompany } from './cartTypes';
import {
  IQuotationShipingCompany,
  IQuoteSupplierItems,
  QuoteGetItemsResponse,
  QuoteGetItemConditionsWrapper
} from '@/main/models/response';
import { isEmpty, isNullUndefined } from '@/main/services/utils';

export function findSupplierInCart(guidSupplier: string) {
  const cartState = store.getState().cart;
  return cartState.cartItems.data.find(item => item.guid === guidSupplier);
}
export function findSupplierInAcc(
  itemCurrent: QuoteGetItemsResponse,
  acc: Cart[]
) {
  return acc.find(
    item =>
      item.guid === itemCurrent.guidSupplier &&
      item.deliveryBySupplier === itemCurrent.deliveryBySupplier
  );
}

function getImagePart(itemQuoteSupplier: IQuoteSupplierItems) {
  if (!isEmpty(itemQuoteSupplier.quoteItem.imageUrl)) return itemQuoteSupplier.quoteItem.imageUrl;
  if (!isEmpty(itemQuoteSupplier.imageUrl)) return itemQuoteSupplier.imageUrl;
  return IMAGE_DEFAULT;
}

export function formatPartCart(
  quoteSupplierItems: IQuoteSupplierItems[]
): CartPart[] {
  const mountPartCart = (itemQuoteSupplier: IQuoteSupplierItems): CartPart => ({
    id: String(itemQuoteSupplier.guid).concat(
      String(itemQuoteSupplier.idPartType)
    ),
    priceTotalFormatted: itemQuoteSupplier.priceTotalFormatted,
    guid: itemQuoteSupplier.quoteItem.guid,
    guidPart: itemQuoteSupplier.quoteItem.guidPart,
    idPartType: itemQuoteSupplier.idPartType,
    guidSupplierInventoryWarehouse:
      itemQuoteSupplier.guidSupplierInventoryWarehouse,
    guidSupplierInventory: itemQuoteSupplier.guidSupplierInventory,
    currency: 'USD',
    deliveryDeadline: itemQuoteSupplier.deliveryDeadline,
    partType: itemQuoteSupplier.partType,
    shippingCost: 0,
    quantity: itemQuoteSupplier.quantity,
    stock: itemQuoteSupplier.quoteItem.stock,
    price: itemQuoteSupplier.price || 0,
    totalPrice: itemQuoteSupplier.price * itemQuoteSupplier.quantity || 0,
    guidQuoteSupplierItem: itemQuoteSupplier.guid,
    externalPartId: itemQuoteSupplier.quoteItem.externalPartId,
    image: getImagePart(itemQuoteSupplier),
    partBrand: itemQuoteSupplier.partBrand,
    priceCap: itemQuoteSupplier.priceCap,
    priceCapFormatted: itemQuoteSupplier.priceCapFormatted,
    systemReferencePrice: itemQuoteSupplier.systemReferencePrice,
    systemReferencePriceFormatted: itemQuoteSupplier.systemReferencePriceFormatted,
    priceDiffPercent: itemQuoteSupplier.priceDiffPercent,
    priceFormatted: itemQuoteSupplier.priceFormatted,
    guidQuoteItem: itemQuoteSupplier.quoteItem.guid,
    quoteProcurementPartInfo: !isNullUndefined(
      itemQuoteSupplier.quoteItem.quoteProcurementPartInfo
    )
      ? itemQuoteSupplier.quoteItem.quoteProcurementPartInfo
      : undefined
  });
  return quoteSupplierItems.map(item => mountPartCart(item));
}

function getGreaterShippingCompany(
  cartAcumulator: ShipingCompany | undefined,
  currentItem: IQuotationShipingCompany
) {
  if (isNullUndefined(cartAcumulator)) {
    return currentItem;
  }
  if (isNullUndefined(currentItem)) {
    return cartAcumulator;
  }

  const highValue =
    cartAcumulator.quotationShipingCompanyItems[0].deliveryDays >
    currentItem.quotationShipingCompanyItems[0].deliveryDays
      ? cartAcumulator
      : currentItem;

  return highValue;
}

function getGreaterShippingCompanySupplier(
  cartAcumulator: Cart | undefined,
  currentItem: QuoteGetItemsResponse
) {
  if (isNullUndefined(cartAcumulator)) {
    return currentItem.deliveryDaysSupplier;
  }
  const formattedDeliveryDaysSuppliers =
    cartAcumulator.deliveryDaysSupplier ?? 0;
  const formattedCurrentItem = currentItem.deliveryDaysSupplier ?? 0;

  const shippingCompanySupplier =
    formattedDeliveryDaysSuppliers > formattedCurrentItem
      ? cartAcumulator?.deliveryDaysSupplier
      : currentItem.deliveryDaysSupplier;

  return shippingCompanySupplier;
}

export function transformCartItemsToCart(
  resultData: QuoteGetItemsResponse[],
  guidQuote: string
): Cart[] {
  return resultData.reduce((acc: Cart[], item: QuoteGetItemsResponse) => {
    const hasExistsSupplier = findSupplierInAcc(item, acc);
    const formattedParts = formatPartCart(item.quoteSupplierItems);
    const partsSupplier = hasExistsSupplier
      ? [...hasExistsSupplier.parts, ...formattedParts]
      : formattedParts;

    const totalPartsPrice = partsSupplier.reduce(
      (total, item) => total + item.totalPrice,
      0
    );
    const totalParts = partsSupplier.reduce(
      (total, item) => total + item.quantity,
      0
    );

    const shippingCompanyIntegration = hasExistsSupplier
      ? getGreaterShippingCompany(
          hasExistsSupplier.shipingCompany,
          item.quotationShipingCompany
        )
      : !item.shipingIntegrationIsUnavailable
      ? item.quotationShipingCompany
      : undefined;

    const formattedCart: Cart = {
      id: item.guidSupplier,
      guid: item.guidSupplier,
      guidQuote: guidQuote,
      name: item.companyName,
      isFavoriteSupplier: item.isFavoriteSupplier,
      deliveryBySupplier: item.deliveryBySupplier,
      deliveryDaysSupplier:
        getGreaterShippingCompanySupplier(hasExistsSupplier, item) || null,
      totalParts: totalParts,
      rating: item.score,
      shipingCompany: shippingCompanyIntegration,
      totalShippingCost: item.deliveryBySupplier
        ? 0
        : !item.shipingIntegrationIsUnavailable
        ? item.quotationShipingCompany.quotationShipingCompanyItems[0]
            .totalAmount
        : 0,
      totalPartsPrice: totalPartsPrice,
      shipingIntegrationIsUnavailable: item.shipingIntegrationIsUnavailable,
      parts: sortParts(partsSupplier)
    };
    if (hasExistsSupplier) {
      const filterAcc = acc.filter(
        (itemCart: Cart) => itemCart.guid !== item.guidSupplier
      );
      return [...filterAcc, formattedCart];
    }

    return [...acc, formattedCart];
  }, [] as Cart[]);
}

function sortParts(partsSupplier: CartPart[]) {
  return partsSupplier.sort((a, b) => {
    const aQuoteItemDescription = !isNullUndefined(
      a.quoteProcurementPartInfo?.quoteItemDescription
    )
      ? a.quoteProcurementPartInfo?.quoteItemDescription
      : '';
    const bQuoteItemDescription = !isNullUndefined(
      b.quoteProcurementPartInfo?.quoteItemDescription
    )
      ? b.quoteProcurementPartInfo?.quoteItemDescription
      : '';

    if (
      a.quoteProcurementPartInfo?.partName &&
      b.quoteProcurementPartInfo?.partName
    ) {
      return a.quoteProcurementPartInfo?.partName.localeCompare(
        b.quoteProcurementPartInfo?.partName
      );
    }

    return aQuoteItemDescription.localeCompare(bQuoteItemDescription);
  });
}

export function destructGetItemsResponse(
  result: QuoteGetItemConditionsWrapper
) {
  if (isNullUndefined(result)) {
    return {
      dataCart: [],
      dataCartNoSupplierAssigned: null,
      dataCartUnansweredItems: null,
      dataCartUnansweredCanceledItems: null,
      dataCartUnansweredRequotedItems: null
    };
  }

  const dataCart =
    !isNullUndefined(result.assignedItems) && result.assignedItems.length > 0
      ? result.assignedItems
      : [];
  const dataCartNoSupplierAssigned =
    !isNullUndefined(result.unassignedItems) &&
    result.unassignedItems.parts.length > 0
      ? result.unassignedItems
      : null;
  const dataCartUnansweredItems =
    !isNullUndefined(result.unansweredItems) &&
    result.unansweredItems.parts.length > 0
      ? result.unansweredItems
      : null;
  const dataCartUnansweredCanceledItems =
    !isNullUndefined(result.unansweredCanceledItems) &&
    result.unansweredCanceledItems.parts.length > 0
      ? result.unansweredCanceledItems
      : null;
  const dataCartUnansweredRequotedItems =
    !isNullUndefined(result.unansweredRequotedItems) &&
    result.unansweredRequotedItems.parts.length > 0
      ? result.unansweredRequotedItems
      : null;
  return {
    dataCart,
    dataCartNoSupplierAssigned,
    dataCartUnansweredItems,
    dataCartUnansweredCanceledItems,
    dataCartUnansweredRequotedItems
  };
}

export function getGuidsQuoteItemInCart(cart: CartState) {
  const guidsCartItem = cart.cartItems.data
    .flatMap(item => item.parts.map(part => part.guid))
    .filter(item => item !== undefined);
  const guidsCartUnansweredItems =
    cart.cartItems.dataUnansweredItems?.parts.map(part => part.guidQuoteItem) ??
    [];
  const guidsCartNoSupplierAssigned =
    cart.cartItems.dataNoSupplierAssigned?.parts.map(
      part => part.guidQuoteItem
    ) ?? [];

  return [
    ...guidsCartItem,
    ...guidsCartUnansweredItems,
    ...guidsCartNoSupplierAssigned
  ];
}

export function getGuidsQuoteSupplierItemInCart(cart: CartState) {
  const guidsCartItem = cart.cartItems.data
    .flatMap(item => item.parts.map(part => part.guidQuoteSupplierItem))
    .filter(item => item !== undefined);

  return guidsCartItem;
}
export function calculateTotalPrice(items: Cart[]) {
  return items.reduce((acc, item) => acc + item.totalPartsPrice, 0);
}

export function calculateTotalShippingCost(items: Cart[]) {
  return items.reduce((acc, item) => acc + item.totalShippingCost, 0);
}
export function calculateTotalParts(items: Cart[]) {
  return items.reduce((acc, item) => acc + item.totalParts, 0);
}
